import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import { motion } from "framer-motion";

const size = 700;
const outterRadius = size / 2 - 100;
const middleRadius = (outterRadius * 2) / 3;
const innerCircleRadius = outterRadius / 3;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media screen and (max-width: 1000px) {
    margin: auto 10px;
  }

  a {
    color: white;
    :hover {
      border-bottom: 1px solid white;
    }
  }
`;

const RadarWrapper = styled(motion.div)`
  width: 50vw;
  display: inline-block;
  pointer-events: none;
  z-index: 1;
  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

const Title = styled.div`
  font-size: 20px;
  margin: 10px 0 20px 0;
`;

const IntroText = styled(motion.div)`
  font:
    38px "DM Serif Text",
    serif;
  font-weight: 300;
  z-index: 2;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50%;
  max-width: 900px;

  div:nth-child(3) {
    font: 34px Poppins;
    margin: 20px auto;
    font-size: 18px;
    width: 45vw;
    max-width: 900px;
    @media screen and (min-width: 1600px) {
      line-height: 2;
    }
  }

  @media screen and (max-width: 1000px) {
    position: unset;
    width: 100%;
    font-size: 22px;
    div:nth-child(2),
    div:nth-child(3) {
      width: 100%;
      margin: 10px;
    }
    div:nth-child(3) {
      margin-bottom: 50px;
      font-size: 14px;
      width: 100%;
    }
  }
`;

const StyledButton = styled(motion(Link))`
  height: 30px;
  color: white;
  font: 18px Poppins;
  padding: 10px 30px;
  text-align: center;
  border: 2px solid ${({ theme }) => theme.pink} !important;
  border-radius: 40px;
  transition: 0.3s;
  text-decoration: none;
  cursor: pointer;

  :hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    scale: 1.05;
  }

  @media screen and (max-width: 1000px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Home = () => {
  return (
    <Wrapper>
      {/* === Animated Intro Text === */}
      <IntroText
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        {/* === Profile Image + Name === */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <motion.img
            loading="eager"
            src={`${process.env.REACT_APP_S3_BASE_URL}me.jpeg`}
            alt="profile"
            height={120}
            style={{
              borderRadius: 100,
              border: "2px solid white",
              marginRight: 10,
            }}
            whileHover={{ scale: 1.05, rotate: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          />
          <Title>Alexandra Veres</Title>
        </motion.div>

        {/* === Text 1 === */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Welcome to my portfolio, a visual and interactive exploration of my
          work, skills, and interests as a Software Engineer.
        </motion.div>

        {/* === Text 2 === */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          With over 8 years of experience delivering production-grade web
          applications, this site includes a personal Tech Radar that showcases
          the technologies I currently use or am exploring. Beyond code, this
          portfolio reflects my passion for transforming ideas and stories into
          visual experiences that spark curiosity and leave a lasting
          impression.
        </motion.div>

        {/* === Button === */}
        <StyledButton
          to="/portfolio"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
          whileHover={{ scale: 1.07 }}
          whileTap={{ scale: 0.95 }}
        >
          View My Portfolio
        </StyledButton>
      </IntroText>

      {/* === Animated Radar Background === */}
      <RadarWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          opacity: { duration: 2 },
          rotate: { duration: 50, repeat: Infinity, ease: "linear" },
        }}
      >
        <svg viewBox="0 0 600 600" style={{ overflow: "visible" }}>
          <g transform={`translate(300,300)`}>
            <motion.circle
              cx={0}
              cy={0}
              r={outterRadius}
              fill="#ffffff"
              stroke="lightgrey"
              opacity={0.1}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx={0}
              cy={0}
              r={middleRadius}
              fill="#ffffff"
              opacity={0.08}
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx={0}
              cy={0}
              r={innerCircleRadius}
              fill="#ffffff"
              opacity={0.06}
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />
          </g>
        </svg>
      </RadarWrapper>
    </Wrapper>
  );
};

export default Home;
